/*
Provides a default, reusable form that sets and updates properties required to send
an email from within wealthpro through the MSGraph service.
*/

import _ from "lodash";
import React from "react";
import classNames from "classnames";
import EmailContactsPagedSelect, { type EmailContactsSelectOption } from "../selects/EmailContactsPagedSelect";
import UserDelegatesSelect from "../selects/UserDelegatesSelect"
import { MailMessageAttachmentsDisplayForm } from "./MailMessageAttachmentsDisplayForm";
import { MailMessageAttachmentsFileInput } from "./MailMessageAttachmentsFileInput";
import { MailMessageAttachmentsUploadForm } from "./MailMessageAttachmentsUploadForm";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { FormInput, FormLabel, RichTextEditor } from "../";
import { DelegatedAccessTypes } from "../../../helpers/constants"

export type EmailMessageFormProps = UseFormReturn<MailMessage, any> & {
    defaultValues: MailMessage | null | undefined
    isAttachmentsShownFirst?: boolean
    isDocumentSelectorShown?: boolean
    isFileProtectionEnabled?: boolean
    isForward?: boolean
    isReply?: boolean
};

const convertToEmailContactSelectOption = (v: MailMessageRecipient[] | null | undefined) : EmailContactsSelectOption[] => {
    if (!v || _.isEmpty(v)) {
        return [];
    }
    return v.map(el => ({
        label: el.contactName,
        value: el.contactEmailAddress,
        __isNew__: false,
    } as EmailContactsSelectOption));
};

const convertToMailMessageRecipients = (v: EmailContactsSelectOption[]) : MailMessageRecipient[] => {
    return v.map(el => ({
        contactName: el.label,
        contactEmailAddress: el.value,
        isNew: el.__isNew__ ?? false,
        isRestricted: el.data?.isRestricted ?? false
    } as MailMessageRecipient));
};

export const EmailMessageForm = ({ 
    control,
    defaultValues,
    formState, 
    isAttachmentsShownFirst = true, 
    isDocumentSelectorShown = false,
    isFileProtectionEnabled = false,
    isForward = false,
    isReply = false, 
    setValue, 
    watch 
} : Readonly<EmailMessageFormProps>) => {
    const { errors } = formState;
    const from = watch('from');
    const masterAccountId = watch('masterAccountId');
    const attachments = watch('attachments');
    const attachmentsForm = useFieldArray({ control, name: 'attachments' });
    const [toRecipients, setToRecipients] = React.useState<EmailContactsSelectOption[]>(convertToEmailContactSelectOption(defaultValues?.toRecipients));
    const [ccRecipients, setCcRecipients] = React.useState<EmailContactsSelectOption[]>(convertToEmailContactSelectOption(defaultValues?.ccRecipients));
    const [bcRecipients, setBcRecipients] = React.useState<EmailContactsSelectOption[]>(convertToEmailContactSelectOption(defaultValues?.bccRecipients));

    React.useEffect(() => setValue('toRecipients', convertToMailMessageRecipients(toRecipients), { shouldDirty: true }), [ toRecipients ]);
    React.useEffect(() => setValue('ccRecipients', convertToMailMessageRecipients(ccRecipients), { shouldDirty: true }), [ ccRecipients ]);
    React.useEffect(() => setValue('bccRecipients', convertToMailMessageRecipients(bcRecipients), { shouldDirty: true }), [ bcRecipients ]);

    const isAnyAttachments = (attachments && attachments.length > 0);
    return (
        <>
            {!isForward && (
                <div className="row d-flex align-items-center mb-2">
                    <div className="col-2 col-lg-1">
                        <FormLabel>From</FormLabel>
                    </div>
                    <div className="col">
                        <Controller
                            name="from"
                            control={control}
                            rules={{ required: "Sender is required." }}
                            render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                <UserDelegatesSelect
                                    accessType={DelegatedAccessTypes.writeMail}
                                    defaultValue={value}
                                    errorAllowRetry={false}
                                    errorMessage={errors?.from?.message}
                                    onChange={(opt: SelectOption<string>) => setValue('from', opt.value, { shouldDirty: true })}
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            <div className="row d-flex align-items-center mb-2">
                <div className="col-2 col-lg-1">
                    <FormLabel>To</FormLabel>
                </div>
                <div className="col">
                    <Controller
                        name="toRecipients"
                        control={control}
                        rules={{ required: "To recipients are required." }}
                        render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                            <EmailContactsPagedSelect
                                key={from}
                                userId={from}
                                masterAccountIds={masterAccountId ? [masterAccountId] : null}
                                closeMenuOnSelect={false}
                                value={toRecipients}
                                onChange={(opts: any) => setToRecipients(() => opts)}
                                {...rest}
                            />
                        )}
                    />
                </div>
            </div>
            {!isForward && (
                <div className="row d-flex align-items-center mb-2">
                    <div className="col-2 col-lg-1">
                        <FormLabel>Cc</FormLabel>
                    </div>
                    <div className="col">
                        <Controller
                            name="ccRecipients"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                <EmailContactsPagedSelect
                                    key={from}
                                    userId={from}
                                    masterAccountIds={masterAccountId ? [masterAccountId] : null}
                                    value={ccRecipients}
                                    onChange={(opts: any) => setCcRecipients(() => opts)}
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            {!isForward && !isReply && (
                <div className="row d-flex align-items-center mb-2">
                    <div className="col-2 col-lg-1">
                        <FormLabel>Subject</FormLabel>
                    </div>
                    <div className="col">
                        <Controller
                            name="subject"
                            control={control}
                            rules={{ required: "Subject is required." }}
                            render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                <FormInput
                                    value={value}
                                    errorAllowRetry={false}
                                    errorMessage={errors?.subject?.message}
                                    placeholder="Add a subject"
                                    onChange={(e: any) => setValue('subject', e.target.value, { shouldDirty: true })}
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            {!isForward && !isDocumentSelectorShown && (
                <MailMessageAttachmentsUploadForm control={control} setValue={setValue} {...attachmentsForm} />
            )}
            {!isForward && isDocumentSelectorShown && (
                <MailMessageAttachmentsFileInput 
                    control={control} 
                    masterAccountId={masterAccountId}
                    setValue={setValue} 
                    {...attachmentsForm} 
                />
            )}
            <div className={classNames('d-flex', { 'flex-column-reverse': isAttachmentsShownFirst, 'flex-column': !isAttachmentsShownFirst })}>
                <div className={classNames({ 'mt-2': isAttachmentsShownFirst && isAnyAttachments, 'mb-2': !isAttachmentsShownFirst && isAnyAttachments })}>
                    <Controller
                        name="body"
                        control={control}
                        rules={{ required: "Body is required." }}
                        render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                            <RichTextEditor
                                maxHeight={"300px"}
                                height={"300px"}
                                defaultValue={value}
                                errorAllowRetry={false}
                                disableAnimations
                                errorMessage={errors?.body?.message}
                                onChange={(e: any) => setValue('body', e, { shouldDirty: true })}
                                {...rest}
                            />
                        )}
                    />
                </div>
                {!isForward && (
                    <MailMessageAttachmentsDisplayForm 
                        control={control} 
                        setValue={setValue} 
                        enableFileProtection={isFileProtectionEnabled}
                        {...attachmentsForm} 
                    />
                )}
            </div>
        </>
    );
};

export default EmailMessageForm;